(() => {
  const ELEMENT_NAME = 'cc-t10-speed-purity-carousel';
  const MOBILE_QUERY = '(max-width: 768px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const SWIPE_THRESHOLD = 36;
  const DIRECTION_RATIO = 1.25;
  const DECODE_TIMEOUT = 1200;
  const MOTION_DURATION = 400;
  const MOTION_EASING = 'ease-in-out';

  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class CCT10SpeedPurityCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedReady) {
        return;
      }

      this.isConnectedReady = true;
      this.lifecycleController = new AbortController();
      this.modeController = null;
      this.revealObserver = null;
      this.preloadObserver = null;
      this.intentToken = 0;
      this.motionSequence = 0;
      this.activeIndex = 0;
      this.pointerState = null;
      this.pendingTimers = new Set();
      this.pendingImageFinishes = new Set();
      this.decodedImages = new Set();
      this.activeAnimations = new Set();
      this.hasWarmedImages = false;

      this.section = this.closest('[data-cc-t10-speed-purity]');
      this.viewport = this.querySelector('[data-cc-t10-speed-purity-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-cc-t10-speed-purity-slide]'));
      this.indicators = Array.from(this.querySelectorAll('[data-cc-t10-speed-purity-indicator]'));
      this.previousButton = this.querySelector('[data-cc-t10-speed-purity-previous]');
      this.nextButton = this.querySelector('[data-cc-t10-speed-purity-next]');
      this.previousImage = this.previousButton?.querySelector('[data-cc-t10-speed-purity-preview-image]');
      this.nextImage = this.nextButton?.querySelector('[data-cc-t10-speed-purity-preview-image]');
      this.previousCurrentLayer = this.previousButton?.querySelector('[data-cc-t10-speed-purity-preview-current]');
      this.nextCurrentLayer = this.nextButton?.querySelector('[data-cc-t10-speed-purity-preview-current]');
      this.previousIncomingLayer = this.previousButton?.querySelector('[data-cc-t10-speed-purity-preview-incoming]');
      this.nextIncomingLayer = this.nextButton?.querySelector('[data-cc-t10-speed-purity-preview-incoming]');
      this.previousIncomingImage = this.previousButton?.querySelector('[data-cc-t10-speed-purity-preview-incoming-image]');
      this.nextIncomingImage = this.nextButton?.querySelector('[data-cc-t10-speed-purity-preview-incoming-image]');
      this.status = this.querySelector('[data-cc-t10-speed-purity-status]');
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

      if (!this.section || !this.viewport || this.slides.length === 0 || this.indicators.length !== this.slides.length) {
        return;
      }

      this.bindCommonControls();
      this.bindMediaQueries();
      this.setupReveal();
      this.setupImageWarming();
      this.initializeMode();
      this.classList.add('is-cc-controller-ready');
    }

    disconnectedCallback() {
      this.isConnectedReady = false;
      this.intentToken += 1;
      this.modeController?.abort();
      this.lifecycleController?.abort();
      this.revealObserver?.disconnect();
      this.preloadObserver?.disconnect();
      this.cancelDesktopMotion(false);
      this.pendingImageFinishes?.forEach((finish) => finish());
      this.pendingImageFinishes?.clear();
      this.pendingTimers?.clear();
      this.decodedImages?.clear();
      this.pointerState = null;
    }

    bindCommonControls() {
      const signal = this.lifecycleController.signal;

      this.previousButton?.addEventListener('click', () => {
        void this.navigate(this.activeIndex - 1, true);
      }, { signal });

      this.nextButton?.addEventListener('click', () => {
        void this.navigate(this.activeIndex + 1, true);
      }, { signal });

      this.indicators.forEach((indicator, index) => {
        indicator.addEventListener('click', () => {
          void this.navigate(index, true);
        }, { signal });
      });

      this.viewport.addEventListener('keydown', (event) => {
        if (event.target.closest('button')) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          void this.navigate(this.activeIndex - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          void this.navigate(this.activeIndex + 1, true);
        }
      }, { signal });

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-cc-t10-speed-purity-slide]');
        const index = this.slides.indexOf(slide);

        if (index >= 0) {
          void this.navigate(index, false);
        }
      }, { signal });
    }

    bindMediaQueries() {
      const handleModeChange = () => this.initializeMode();
      const handleMotionChange = () => {
        if (this.reducedMotionMedia.matches) {
          this.cancelDesktopMotion(true);
          this.reveal();
        }
      };

      if (typeof this.mobileMedia.addEventListener === 'function') {
        this.mobileMedia.addEventListener('change', handleModeChange);
        this.reducedMotionMedia.addEventListener('change', handleMotionChange);
        this.lifecycleController.signal.addEventListener('abort', () => {
          this.mobileMedia.removeEventListener('change', handleModeChange);
          this.reducedMotionMedia.removeEventListener('change', handleMotionChange);
        }, { once: true });
      } else {
        this.mobileMedia.addListener(handleModeChange);
        this.reducedMotionMedia.addListener(handleMotionChange);
        this.lifecycleController.signal.addEventListener('abort', () => {
          this.mobileMedia.removeListener(handleModeChange);
          this.reducedMotionMedia.removeListener(handleMotionChange);
        }, { once: true });
      }
    }

    initializeMode() {
      this.cancelDesktopMotion(false);
      this.modeController?.abort();
      this.modeController = new AbortController();
      this.pointerState = null;
      this.intentToken += 1;

      const isMobile = this.mobileMedia.matches;
      const defaultIndex = isMobile ? 0 : Math.min(1, this.slides.length - 1);

      this.dataset.ccMode = isMobile ? 'mobile' : 'desktop';
      this.applyState(defaultIndex, false, false);

      if (isMobile) {
        this.bindMobileSwipe(this.modeController.signal);
      }

      if (this.hasWarmedImages) {
        this.warmNeighborImages();
      }
    }

    bindMobileSwipe(signal) {
      const resetPointer = () => {
        this.pointerState = null;
      };

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) {
          return;
        }

        this.pointerState = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY
        };
      }, { signal });

      this.viewport.addEventListener('pointermove', (event) => {
        if (!this.pointerState || this.pointerState.id !== event.pointerId) {
          return;
        }

        this.pointerState.lastX = event.clientX;
        this.pointerState.lastY = event.clientY;
      }, { signal });

      this.viewport.addEventListener('pointerup', (event) => {
        if (!this.pointerState || this.pointerState.id !== event.pointerId) {
          return;
        }

        const deltaX = event.clientX - this.pointerState.startX;
        const deltaY = event.clientY - this.pointerState.startY;
        resetPointer();

        if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY) * DIRECTION_RATIO) {
          return;
        }

        void this.navigate(this.activeIndex + (deltaX < 0 ? 1 : -1), true);
      }, { signal });

      this.viewport.addEventListener('pointercancel', resetPointer, { signal });
      this.viewport.addEventListener('lostpointercapture', resetPointer, { signal });
      this.viewport.addEventListener('touchcancel', resetPointer, { signal, passive: true });
    }

    normalizeIndex(index) {
      return (index + this.slides.length) % this.slides.length;
    }

    async navigate(targetIndex, announce) {
      const normalizedIndex = this.normalizeIndex(targetIndex);
      const direction = targetIndex >= this.activeIndex ? 'next' : 'previous';
      const token = ++this.intentToken;

      if (normalizedIndex === this.activeIndex) {
        return;
      }

      await this.ensureImageReady(normalizedIndex);

      if (token !== this.intentToken || !this.isConnectedReady) {
        return;
      }

      if (this.mobileMedia.matches || this.reducedMotionMedia.matches || typeof Element.prototype.animate !== 'function') {
        this.cancelDesktopMotion(false);
        this.applyState(normalizedIndex, true, announce);
      } else {
        this.playDesktopMotion(normalizedIndex, direction, announce, token);
      }

      this.warmNeighborImages();
    }

    applyState(index, animate, announce, deferPreviewUpdate = false) {
      this.activeIndex = index;
      this.dataset.ccActiveIndex = String(index);
      this.toggleAttribute('data-cc-animate', animate && !this.reducedMotionMedia.matches);

      this.slides.forEach((slide, slideIndex) => {
        const distance = (slideIndex - index + this.slides.length) % this.slides.length;
        const isActive = distance === 0;

        slide.classList.toggle('is-cc-active', isActive);
        slide.classList.toggle('is-cc-previous', distance === this.slides.length - 1);
        slide.classList.toggle('is-cc-next', distance === 1);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('inert', !isActive);
      });

      this.indicators.forEach((indicator, indicatorIndex) => {
        if (indicatorIndex === index) {
          indicator.setAttribute('aria-current', 'true');
        } else {
          indicator.removeAttribute('aria-current');
        }
      });

      if (!deferPreviewUpdate) {
        this.updatePreviewControls();
      }

      if (announce && this.status) {
        const title = this.slides[index].dataset.ccSlideTitle || '';
        this.status.textContent = `${index + 1}／${this.slides.length}：${title}`;
      }
    }

    playDesktopMotion(index, direction, announce, intentToken) {
      const outgoingSlide = this.slides[this.activeIndex];
      const incomingSlide = this.slides[index];

      if (!outgoingSlide || !incomingSlide) {
        this.applyState(index, false, announce);
        return;
      }

      this.cancelDesktopMotion(true);
      const motionSequence = ++this.motionSequence;
      const travel = direction === 'next' ? -100 : 100;
      const incomingStart = travel * -1;

      this.prepareIncomingPreviews(index);
      this.applyState(index, false, announce, true);
      outgoingSlide.classList.add('is-cc-motion-outgoing');

      const options = {
        duration: MOTION_DURATION,
        easing: MOTION_EASING,
        fill: 'both'
      };
      const animations = [];

      animations.push(outgoingSlide.animate([
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${travel}%, 0, 0)` }
      ], options));
      animations.push(incomingSlide.animate([
        { transform: `translate3d(${incomingStart}%, 0, 0)` },
        { transform: 'translate3d(0, 0, 0)' }
      ], options));

      [
        [this.previousCurrentLayer, this.previousIncomingLayer],
        [this.nextCurrentLayer, this.nextIncomingLayer]
      ].forEach(([currentLayer, incomingLayer]) => {
        if (!currentLayer || !incomingLayer) {
          return;
        }

        incomingLayer.style.visibility = 'visible';
        animations.push(currentLayer.animate([
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(${travel}%, 0, 0)` }
        ], options));
        animations.push(incomingLayer.animate([
          { transform: `translate3d(${incomingStart}%, 0, 0)` },
          { transform: 'translate3d(0, 0, 0)' }
        ], options));
      });

      animations.forEach((animation) => this.activeAnimations.add(animation));

      Promise.all(animations.map((animation) => animation.finished.catch(() => null))).then(() => {
        if (motionSequence !== this.motionSequence || intentToken !== this.intentToken || !this.isConnectedReady) {
          return;
        }

        animations.forEach((animation) => {
          animation.cancel();
          this.activeAnimations.delete(animation);
        });
        outgoingSlide.classList.remove('is-cc-motion-outgoing');
        this.resetPreviewLayers();
        this.updatePreviewControls();
      });
    }

    prepareIncomingPreviews(index) {
      const previousSlide = this.slides[this.normalizeIndex(index - 1)];
      const nextSlide = this.slides[this.normalizeIndex(index + 1)];

      if (previousSlide) {
        this.previousButton?.setAttribute('aria-label', `上一項：${previousSlide.dataset.ccSlideTitle || ''}`);
        this.updatePreviewImage(this.previousIncomingImage, previousSlide);
      }

      if (nextSlide) {
        this.nextButton?.setAttribute('aria-label', `下一項：${nextSlide.dataset.ccSlideTitle || ''}`);
        this.updatePreviewImage(this.nextIncomingImage, nextSlide);
      }
    }

    resetPreviewLayers() {
      [this.previousCurrentLayer, this.nextCurrentLayer].forEach((layer) => {
        if (layer) {
          layer.style.transform = '';
        }
      });

      [this.previousIncomingLayer, this.nextIncomingLayer].forEach((layer) => {
        if (layer) {
          layer.style.transform = '';
          layer.style.visibility = '';
        }
      });
    }

    cancelDesktopMotion(syncPreviews = true) {
      this.motionSequence = (this.motionSequence || 0) + 1;
      this.activeAnimations?.forEach((animation) => animation.cancel());
      this.activeAnimations?.clear();
      this.slides?.forEach((slide) => slide.classList.remove('is-cc-motion-outgoing'));
      this.resetPreviewLayers?.();

      if (syncPreviews && this.slides?.length) {
        this.updatePreviewControls();
      }
    }

    updatePreviewControls() {
      const previousIndex = this.normalizeIndex(this.activeIndex - 1);
      const nextIndex = this.normalizeIndex(this.activeIndex + 1);
      const previousSlide = this.slides[previousIndex];
      const nextSlide = this.slides[nextIndex];

      if (this.previousButton && previousSlide) {
        this.previousButton.setAttribute('aria-label', `上一項：${previousSlide.dataset.ccSlideTitle || ''}`);
        this.updatePreviewImage(this.previousImage, previousSlide);
      }

      if (this.nextButton && nextSlide) {
        this.nextButton.setAttribute('aria-label', `下一項：${nextSlide.dataset.ccSlideTitle || ''}`);
        this.updatePreviewImage(this.nextImage, nextSlide);
      }
    }

    updatePreviewImage(image, slide) {
      const source = slide.dataset.ccDesktopPreviewSrc;

      if (image && source && image.getAttribute('src') !== source) {
        image.src = source;
      }
    }

    getSlideImage(index) {
      return this.slides[this.normalizeIndex(index)]?.querySelector('[data-cc-t10-speed-purity-image]') || null;
    }

    warmNeighborImages() {
      [this.activeIndex, this.activeIndex - 1, this.activeIndex + 1].forEach((index) => {
        const image = this.getSlideImage(index);

        if (!image) {
          return;
        }

        image.loading = 'eager';

        if (image.complete && image.naturalWidth > 0) {
          this.decodedImages.add(image);
          return;
        }

        if (typeof image.decode === 'function') {
          image.decode().catch(() => {}).then(() => {
            if (image.complete && image.naturalWidth > 0 && this.isConnectedReady) {
              this.decodedImages.add(image);
            }
          });
        }
      });
    }

    async ensureImageReady(index) {
      const image = this.getSlideImage(index);

      if (!image || this.decodedImages.has(image)) {
        return true;
      }

      image.loading = 'eager';

      if (image.complete && image.naturalWidth > 0) {
        if (typeof image.decode === 'function') {
          try {
            await image.decode();
          } catch (error) {
            // complete + naturalWidth is the approved Safari-safe fallback.
          }
        }

        this.decodedImages.add(image);
        return true;
      }

      return new Promise((resolve) => {
        let settled = false;
        let timer = null;

        const finish = () => {
          if (settled) {
            return;
          }

          settled = true;
          window.clearTimeout(timer);
          this.pendingTimers.delete(timer);
          this.pendingImageFinishes.delete(finish);

          if (image.complete && image.naturalWidth > 0) {
            this.decodedImages.add(image);
          }

          resolve(image.complete && image.naturalWidth > 0);
        };

        image.addEventListener('load', finish, { once: true, signal: this.lifecycleController.signal });
        image.addEventListener('error', finish, { once: true, signal: this.lifecycleController.signal });

        timer = window.setTimeout(finish, DECODE_TIMEOUT);
        this.pendingTimers.add(timer);
        this.pendingImageFinishes.add(finish);

        if (typeof image.decode === 'function') {
          image.decode().then(finish).catch(() => {
            if (image.complete && image.naturalWidth > 0) {
              finish();
            }
          });
        }
      });
    }

    setupImageWarming() {
      if (!('IntersectionObserver' in window)) {
        this.hasWarmedImages = true;
        this.warmNeighborImages();
        return;
      }

      this.preloadObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.hasWarmedImages = true;
        this.warmNeighborImages();
        this.preloadObserver?.disconnect();
        this.preloadObserver = null;
      }, { threshold: 0.01, rootMargin: '100% 0px 100% 0px' });

      this.preloadObserver.observe(this.section);
    }

    setupReveal() {
      if (this.section.dataset.ccRevealEnabled !== 'true' || this.reducedMotionMedia.matches || !('IntersectionObserver' in window)) {
        this.reveal();
        return;
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.reveal();
        this.revealObserver?.disconnect();
        this.revealObserver = null;
      }, { threshold: 0.24, rootMargin: '0px 0px -24% 0px' });

      this.revealObserver.observe(this.section);
    }

    reveal() {
      this.section?.classList.add('is-cc-revealed');
    }
  }

  customElements.define(ELEMENT_NAME, CCT10SpeedPurityCarousel);
})();
